import {
  Dialog,
  Disclosure,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import CloseIcon from '@mui/icons-material/Close';
import { Fragment } from 'react';

import { useTranslate } from '../../hooks/useTranslate';
import { AccountCircleIcon } from '../../icons/AccountCircleIcon';
import { ContactSupportIcon } from '../../icons/ContactSupportIcon';
import { DirectionsCarIcon } from '../../icons/DirectionsCarIcon';
import { EditCalendarIcon } from '../../icons/EditCalendarIcon';
import { HandshakeIcon } from '../../icons/HandshakeIcon';
import { LockIcon } from '../../icons/LockIcon';
import { MyTripsIcon } from '../../icons/MyTripsIcon';
import { RedeemIcon } from '../../icons/RedeemIcon';
import { SembonusIcon } from '../../icons/SembonusIcon';
import { TagIcon } from '../../icons/TagIcon';
import { TravelExploreIcon } from '../../icons/TravelExploreIcon';
import { TravelIcon } from '../../icons/TravelIcon';
import { useMarket } from '../../providers/MarketProvider';
import { useSembonusUser } from '../../providers/UserProvider';
import { AVAILABLE_SEMBONUS_MARKETS } from '../../utils/constants';
import { GetTagsByMarketResult } from '../MarketPage/MarketPage.types';
import { MARKET_URLS_MAP } from './SideMenu.constants';
import {
  Backdrop,
  Chevron,
  CloseButton,
  Content,
  Group,
  Header,
  Link,
  Menu,
  MenuContainer,
  NestedGroup,
  Separator,
  StyledButton,
  ToggleExpandButton,
  TransitionDiv,
} from './SideMenu.styles';

interface Props {
  isOpen: boolean;
  tags: GetTagsByMarketResult | null | undefined;
  close: () => void;
  onAuthClick: (origin: string) => void;
}

export const SideMenu = ({ isOpen, tags, close, onAuthClick }: Props) => {
  const market = useMarket();
  const { t } = useTranslate();
  const { isLoggedIn, userInfo } = useSembonusUser();

  const customUrlMap = MARKET_URLS_MAP[market?.host];

  return (
    <Transition as={Fragment} show={isOpen} appear>
      <Dialog as="div" onClose={close}>
        <Backdrop />

        <MenuContainer>
          <TransitionChild
            as={Fragment}
            enter="transition"
            enterFrom="out"
            enterTo="in"
            leave="transition"
            leaveFrom="in"
            leaveTo="out"
          >
            <TransitionDiv>
              <Menu>
                <Header>
                  <CloseButton type="button" onClick={close}>
                    <CloseIcon />
                  </CloseButton>
                </Header>

                <Content>
                  <Group>
                    <Link href="/places" onClick={close}>
                      <TravelExploreIcon />

                      {t('explore.destinations')}
                    </Link>

                    {tags && !!tags.length && (
                      <Disclosure>
                        <ToggleExpandButton>
                          <TagIcon />

                          {t('explore.popularCategories')}

                          <Chevron data-indicator fontSize="small" />
                        </ToggleExpandButton>

                        <NestedGroup transition>
                          {tags.slice(0, 7).map((tag) => (
                            <Link
                              key={tag._id}
                              href={`/tag/${tag.slug}`}
                              onClick={close}
                            >
                              {tag.name}
                            </Link>
                          ))}
                          <Link href={`/tags`} onClick={close}>
                            <strong>{t('explore.showAllCategories')}</strong>
                          </Link>
                        </NestedGroup>
                      </Disclosure>
                    )}

                    <Link href="/my-trips" onClick={close}>
                      <MyTripsIcon /> {t('ui.navigation.myTrips')}
                    </Link>

                    <Link href="/travel/plan/car-trip">
                      <DirectionsCarIcon />

                      {t('explore.destinationSearch.byCar')}
                    </Link>

                    <Link href="/flight" onClick={close}>
                      <TravelIcon />

                      {t('explore.destinationSearch.flight')}
                    </Link>
                  </Group>

                  {customUrlMap && (
                    <>
                      <Separator />

                      <Group>
                        <Link
                          href={customUrlMap.customerService}
                          target="_blank"
                        >
                          <ContactSupportIcon />

                          {t('explore.header.support')}
                        </Link>

                        <Link href="/my-booking" onClick={close}>
                          <EditCalendarIcon />

                          {t('explore.header.findYourBooking')}
                        </Link>

                        {customUrlMap.giftCard && (
                          <Link href={customUrlMap.giftCard} onClick={close}>
                            <RedeemIcon />

                            {t('explore.navigation.giftCard')}
                          </Link>
                        )}

                        {AVAILABLE_SEMBONUS_MARKETS.includes(market.host) && (
                          <Link href="/sembonus" onClick={close}>
                            <SembonusIcon />
                            {t('explore.navigation.sembonus')}
                          </Link>
                        )}
                        <Link
                          href="https://affiliate-program.sembo.com/"
                          target="_blank"
                        >
                          <HandshakeIcon />
                          {t('explore.navigation.affiliateProgram')}
                        </Link>
                      </Group>
                    </>
                  )}

                  <Separator />

                  <Group>
                    {isLoggedIn ? (
                      <>
                        <Link href="/travel/ob/members/profile">
                          <AccountCircleIcon />

                          {t('explore.hamburger.myProfile')}
                        </Link>

                        {userInfo?.member.travelAgencyId && (
                          <Link
                            href={'https://sembok2.sembo.se/'}
                            target="_blank"
                          >
                            <LockIcon />

                            {t('explore.navigation.travelAgentAccount')}
                          </Link>
                        )}
                      </>
                    ) : (
                      <StyledButton
                        type="button"
                        onClick={() => onAuthClick('side-menu')}
                      >
                        <AccountCircleIcon />

                        {t('explore.hamburger.loginOrRegister')}
                      </StyledButton>
                    )}
                  </Group>
                </Content>
              </Menu>
            </TransitionDiv>
          </TransitionChild>
        </MenuContainer>
      </Dialog>
    </Transition>
  );
};
